import * as Sentry from '@sentry/browser';
import RSDK from '@sr/functions/rsdk/src';
import { BlockListSection, Resume } from '@sr/types';
import React from 'react';
import { AddComboButton, MarkedInput } from '../../ui';
import API from '../../utils/api';
import PlainText from '../tiptap-text/PlainText';
import getContentTypeLabel from './get-content-type-label';
import ItemFormContainer from './ItemFormContainer';
import SectionFormContainer from './SectionFormContainer';

type Props = {
  resume: Resume;
  section: BlockListSection;
};

const BlockListForm = ({ resume, section }: Props) => {
  function createSectionItem() {
    API.sectionItem.create({ section: section });
    API.events.logEvent({
      userId: resume.owner,
      eventName: 'Resume Block List Item Added',
    });
  }

  function handleChange(
    e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    const path = `${resume.id}/sections/${section.sectionId}/items/${e.currentTarget.name}`;
    API.resume.setProperty({
      path,
      value: e.currentTarget.value,
    });
  }

  function updaterSecondaryContent(value: string, itemId: string) {
    API.resume.setProperty({
      path: `${resume.id}/sections/${section.sectionId}/items/${itemId}/secondary`,
      value,
    });
  }

  return (
    <SectionFormContainer resume={resume} sectionId={section.sectionId}>
      {(section.itemsOrder || []).map((itemId) => {
        const item = (section.items || {})[itemId];
        // This should always be true. It is needed for Flow to be able to figure
        // out is is type block-list.
        if (item.type === 'block-list') {
          const secondarySrId = RSDK.getItemPropSrId({
            itemId,
            sectionType: section.type,
            contentType: section.contentType,
            prop: 'subtitle',
          });

          const secondaryPlaceholder = getContentTypeLabel(
            'subtitle',
            section.contentType
          );
          return (
            <ItemFormContainer itemId={itemId} section={section} key={itemId}>
              <MarkedInput
                placeholder={getContentTypeLabel('title', section.contentType)}
                value={item.primary}
                name={`${item.id}/primary`}
                onChange={handleChange}
                srId={RSDK.getItemPropSrId({
                  itemId,
                  sectionType: section.type,
                  contentType: section.contentType,
                  prop: 'title',
                })}
              />
              <PlainText
                content={item.secondary}
                placeholder={secondaryPlaceholder}
                srId={secondarySrId}
                updateContent={(value: string) =>
                  updaterSecondaryContent(value, itemId)
                }
              />
            </ItemFormContainer>
          );
        } else {
          Sentry.captureException(
            new Error(
              "Invalid data. BlockListItem should have same SectionType 'block-list' as parent Section."
            )
          );
          return false;
        }
      })}
      <AddComboButton
        onClick={createSectionItem}
        type="button"
        mt={-4}
        mb={7}
        ml={-4}
        data-testid={`add-${section.contentType}`}
      >
        {getContentTypeLabel('add', section.contentType)}
      </AddComboButton>
    </SectionFormContainer>
  );
};

export default BlockListForm;
